import type { SharedUiColorNameV2 } from '@/shared/ui/theming';
import type { ButtonStyle } from '@/ui/button/ui-button';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { observeControlValidity$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { createControlValidator } from '@/utils/angular/forms/reactive-forms/validators/validators.helpers';
import { ChangeDetectionStrategy, Component, type OnInit, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../../advizeo-modal-spawner/advizeo-modal-spawner.component';

export interface ConfirmActionPopoverInput {
    title?: string;
    subtitle?: string;
    textToType: string;
    buttons: OptionButton[];
}

interface OptionButton {
    text: string;
    code: string;
    requiresValidation?: boolean;
    color: SharedUiColorNameV2;
    buttonStyle?: ButtonStyle;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./confirm-action-popover.scss'],
    selector: 'confirm-action-popover',
    templateUrl: 'confirm-action-popover.html',
    imports: [FormsModule, UiFormFieldComponent, AdvizeoInputDirective, ReactiveFormsModule, UiFormErrorComponent, TranslatePipe]
})
export class ConfirmActionPopoverComponent implements AdvizeoModalComponent<ConfirmActionPopoverInput, string>, OnInit {
    title = signal('');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    input = injectAdvizeoModalInput<ConfirmActionPopoverInput>();
    close = output<string | undefined>();

    control!: FormControl<string>;

    ngOnInit() {
        this.title.set(this.input().title ?? '');
        this.control = new FormControl('', {
            nonNullable: true,
            validators: [
                createControlValidator((control: FormControl<string>) =>
                    control.value.toLowerCase() === this.input().textToType.toLowerCase() ? null : { wrongValidationMessage: true }
                )
            ]
        });

        observeControlValidity$(this.control)
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe(isValid => {
                this.buttons.set(
                    this.input().buttons.map(button => {
                        return {
                            text: button.text,
                            color: button.color,
                            buttonStyle: button.buttonStyle,
                            onClick: () => this.close.emit(button.code),
                            isDisabled: button.requiresValidation !== false && !isValid
                        };
                    })
                );
            });
    }
}
